import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Paper,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { SelectChangeEvent } from '@mui/material/Select';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import api from "../../services/api";
import { Tenant, TenantType, } from "@/src/interfaces";
import { useCryptoNavigation, useAppNavigation } from "../../App";
import HorseLoader from "../../components/loadingHorse";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { UserData } from "@/src/config";
import { supportedLanguages } from "../../i18n";
import { useScope, normalizarId } from "../../hooks/useScope";
import { useListaParceiros } from "../../hooks/useListaParceiros";
import PhoneUtils from "../../utils/phoneUtils";
import {
  formatFiscalCode,
  stripFiscalMask,
  validateFiscalCode,
  formatZipcode,
  exemploCodigoPostal,
  maxLengthCodigoPostal,
  codigoPostalValido,
  normalizarCodigoPostal,
  descreverFormatoFiscal,
} from "../../utils/fiscalCode";

export default function TenantForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation();
  const { getParam } = useAppNavigation();
  const isMobile = useIsMobile();
  
  // Obtém parâmetros do sistema criptografado
  const id = getParam('id');
  const mode = getParam('mode');
  
  const isEditMode = mode === 'edit' && Boolean(id);

  // Verificar role do usuário
  const getUserRole = (): string => {
    try {
      const userStr = localStorage.getItem("user")
      if (userStr) {
        const userData = JSON.parse(userStr) as UserData
        return userData.Role || "0"
      }
    } catch (error) {
      console.error("Erro ao obter role do usuário:", error)
    }
    return "0"
  }

  const userRole = getUserRole()
  // Quem pertence a um parceiro TERCEIRO não escolhe: o vínculo é o dele e
  // ponto. Só o grupo raiz (super admin ou operação principal) escolhe — e só
  // para ele a lista de parceiros é buscada.
  const escopo = useScope()
  const podeEscolherParceiro = escopo.isRootPartner
  const { parceiros, carregando: carregandoParceiros } =
    useListaParceiros(podeEscolherParceiro)
  const readOnlyRoles = ["Viewer", "Alert"]
  const isReadOnlyUser = readOnlyRoles.includes(userRole)
  
  const [tenantTypes, setTenantTypes] = useState<TenantType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Estado do formulário
  const [formData, setFormData] = useState<Tenant>({
    tenantId: undefined,
    tenantName: '',
    tenantNickName: '',
    tenantType: 0,
    tenantPhone: '',
    tenantAddress: '',
    tenantCity: '',
    tenantState: '',
    tenantZipcode: '',
    tenantCountry: '',
    tenantFiscalCode: '',
    tenantLanguage: null,
    // Parceiro do proprietário. NULO = sem vínculo; ZERO = grupo raiz. São
    // coisas diferentes, por isso nada de `?? 0` nem valor-verdade.
    tenantPartnerId: null,
    tenantStatus: 1,
  } as Tenant);

  console.log("🔍 DEBUG TenantForm:");
  console.log("- Parâmetro id (criptografado):", id);
  console.log("- Parâmetro mode (criptografado):", mode);
  console.log("- isEditMode:", isEditMode);



  // Carregar tipos de tenant
  useEffect(() => {
    const fetchTenantTypes = async () => {
      try {
        const response = await api.get("/tenant_type");
        const types = response.data.tenantTypes || response.data || [];
        console.log("Tipos de tenant carregados:", types);
        setTenantTypes(types);
      } catch (error) {
        console.error("Erro ao carregar tipos de tenant:", error);
        setError(t("tenants.loadTypesError"));
      }
    };

    fetchTenantTypes();
  }, []);

  // Carregar dados para edição
  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      api.get(`/tenant/${id}`)
        .then(response => {
          console.log("Dados do tenant recebidos:", response.data);
          const data = response.data.tenant || response.data;
          
          setFormData({
            ...data,
            tenantType: data.tenantType || 0,
            // Aplicar máscaras aos dados carregados
            tenantFiscalCode: data.tenantFiscalCode
              ? formatFiscalCode(data.tenantFiscalCode, data.tenantCountry)
              : '',
            tenantZipcode: data.tenantZipcode
              ? formatZipcode(data.tenantZipcode, data.tenantCountry)
              : '',
            tenantLanguage: data.tenantLanguage ?? null,
            tenantPartnerId: normalizarId(data.tenantPartnerId),
            tenantStatus: data.tenantStatus ?? 1,
          });
        })
        .catch(error => {
          console.error("Erro ao carregar dados do proprietário:", error);
          setError(t("tenants.loadError"));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isEditMode, id]);

  // Função para lidar com mudanças em campos de texto
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    let formattedValue = value;
    
    if (name === 'tenantId') {
  formattedValue = value.replace(/\D/g, '');
}
    // Aplicar máscara conforme o campo
    if (name === 'tenantFiscalCode') {
      formattedValue = formatFiscalCode(value, formData.tenantCountry);
    } else if (name === 'tenantZipcode') {
      formattedValue = formatZipcode(value, formData.tenantCountry);
    } else if (name === 'tenantPhone') {
      formattedValue = PhoneUtils.formatPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
      // Trocar o PAÍS muda a máscara: o código postal já digitado precisa ser
      // reformatado, senão fica com a máscara do país anterior — era o que
      // deixava um código português com a pontuação do CEP brasileiro.
      ...(name === 'tenantCountry'
        ? { tenantZipcode: formatZipcode(prev.tenantZipcode || '', formattedValue) }
        : {}),
    }));
    
    // Limpar erro ao editar o campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Função para lidar com mudanças no select
  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Limpar erro ao editar o campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };
const checkTenantIdExists = async (tenantId: number): Promise<boolean> => {
  try {
    const response = await api.get(`/tenant/${tenantId}`);

    const tenantData = response.data?.tenant || response.data;

    return Boolean(tenantData?.tenantId);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return false;
    }

    console.error("Erro ao verificar tenantId:", error);
    throw error;
  }
};
  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
if (!isEditMode) {
  if (!formData.tenantId) {
    newErrors.tenantId = t("tenants.idRequired");
  } else if (Number(formData.tenantId) <= 0) {
    newErrors.tenantId = t("tenants.idPositive");
  }
}
    // Validar campos obrigatórios
    if (!formData.tenantName?.trim()) {
      newErrors.tenantName = t("tenants.nameRequired");
    }

    if (!formData.tenantNickName?.trim()) {
      newErrors.tenantNickName = t("tenants.nickNameRequired");
    }

    if (!formData.tenantType || formData.tenantType === 0) {
      newErrors.tenantType = t("tenants.typeRequired");
    }

    if (!formData.tenantPhone?.trim()) {
      newErrors.tenantPhone = t("tenants.phoneRequired");
    } else if (!PhoneUtils.validatePhone(formData.tenantPhone)) {
      // Antes o telefone do proprietário não tinha validação NENHUMA — só
      // obrigatoriedade. Agora passa pela mesma regra do cadastro de usuário.
      newErrors.tenantPhone = PhoneUtils.getErrorMessage(formData.tenantPhone);
    }

    if (!formData.tenantAddress?.trim()) {
      newErrors.tenantAddress = t("tenants.addressRequired");
    }

    if (!formData.tenantCity?.trim()) {
      newErrors.tenantCity = t("tenants.cityRequired");
    }

    if (!formData.tenantState?.trim()) {
      newErrors.tenantState = t("tenants.stateRequired");
    }

    if (!formData.tenantZipcode?.trim()) {
      newErrors.tenantZipcode = t("tenants.zipcodeRequired");
    } else {
      // Cada país tem o seu comprimento. Exigir 8 dígitos (o CEP brasileiro)
      // impedia o cadastro de qualquer outro.
      if (!codigoPostalValido(formData.tenantZipcode, formData.tenantCountry)) {
        newErrors.tenantZipcode = t("tenants.zipcodeFormat", {
          mask: exemploCodigoPostal(formData.tenantCountry),
        });
      }
    }

    if (!formData.tenantCountry?.trim()) {
      newErrors.tenantCountry = t("tenants.countryRequired");
    } else if (formData.tenantCountry.trim().length !== 2) {
      newErrors.tenantCountry = t("tenants.countryLength");
    }

    {
      // Brasil segue exigindo 14 dígitos; os demais países aceitam número
      // fiscal alfanumérico de 5 a 20 caracteres.
      const fiscal = validateFiscalCode(
        formData.tenantFiscalCode || '',
        formData.tenantCountry,
      );
      if (!fiscal.valid) {
        const base = t(fiscal.errorKey || "tenants.fiscalCodeRequired");
        // Dizer o formato esperado evita a adivinhação — o erro antigo só
        // dizia que estava errado.
        newErrors.tenantFiscalCode = fiscal.esperado
          ? `${base} (${fiscal.esperado})`
          : base;
      }
    }

    setErrors(newErrors);
    
    // Se há erros, definir mensagem geral
    if (Object.keys(newErrors).length > 0) {
      setError(t("tenants.requiredFieldsError"));
    } else {
      setError(null);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // Função para salvar
  const handleSave = async () => {
  if (!validateForm()) return;

  setSaving(true);
  setError(null);

  try {
    if (!isEditMode) {
      const tenantId = Number(formData.tenantId);

      const exists = await checkTenantIdExists(tenantId);

      if (exists) {
        setErrors(prev => ({
          ...prev,
          tenantId: t("tenants.idExists"),
        }));

        setError(t("tenants.idExistsError"));
        setSaving(false);
        return;
      }
    }

    // O nome do parceiro é derivado do id pelo servidor — devolvê-lo seria
    // mandar um dado que não é nosso para escrever.
    const { tenantPartnerName: _nomeDerivado, ...semNomeDerivado } =
      formData as any;

    const dataToSend = {
      ...semNomeDerivado,
      tenantPartnerId: formData.tenantPartnerId ?? null,
      tenantId: isEditMode ? Number(id) : Number(formData.tenantId),
      tenantType: Number(formData.tenantType),
      // stripFiscalMask preserva LETRAS: número fiscal de fora do Brasil as
      // tem (ex.: PT501234567), e tirar só dígitos o destruiria.
      tenantFiscalCode: stripFiscalMask(formData.tenantFiscalCode || ''),
      tenantZipcode: normalizarCodigoPostal(
        formData.tenantZipcode,
        formData.tenantCountry,
      ),
    };

    console.log("Dados que serão enviados:", dataToSend);

    if (isEditMode) {
      await api.put(`/tenant/${id}`, dataToSend);
    } else {
      await api.post('/tenant', dataToSend);
    }

    navigateToPage('proprietarios', { mode: 'list' });
  } catch (error: any) {
    console.error("Erro ao salvar proprietário:", error);
    const errorMessage =
      error?.response?.data?.message ||
      t("tenants.saveError");
    setError(errorMessage);
  } finally {
    setSaving(false);
  }
};

  // Função para cancelar
  const handleCancel = () => {
    navigateToPage('proprietarios', { mode: 'list' });
  };

  // Exibir loader enquanto carrega dados
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <HorseLoader />
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb">
              <Link 
                color="inherit" 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  handleCancel();
                }}
              >
                {t("nav.tenants")}
              </Link>
              <Typography color="text.primary">
                {isEditMode ? t("tenants.editTitle") : t("tenants.newTitle")}
              </Typography>
            </Breadcrumbs>
            <Typography variant="h5" component="h1" sx={{ mt: 1 }}>
              {isEditMode ? t("tenants.editTitle") : t("tenants.newTitle")}
            </Typography>
          </Box>
          <Button 
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleCancel}
          >
            {t("common.back")}
          </Button>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label={t("tenants.idLabel")}
    name="tenantId"
    type="number"
    required={!isEditMode}
    value={formData.tenantId || ''}
    onChange={handleInputChange}
    error={!!errors.tenantId}
    helperText={
      errors.tenantId ||
      (isEditMode
        ? t("tenants.idLockedHelper")
        : t("tenants.idUniqueHelper"))
    }
    margin="normal"
    disabled={isEditMode || isReadOnlyUser}
    inputProps={{ min: 1 }}
  />
</Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.name")}
                name="tenantName"
                value={formData.tenantName}
                onChange={handleInputChange}
                required
                error={!!errors.tenantName}
                helperText={errors.tenantName}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.nickname")}
                name="tenantNickName"
                required
                value={formData.tenantNickName || ''}
                onChange={handleInputChange}
                error={!!errors.tenantNickName}
                helperText={errors.tenantNickName}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal" 
                required 
                error={!!errors.tenantType}
                disabled={isReadOnlyUser}
              >
                <InputLabel>{t("tenants.typeLabel")}</InputLabel>
                <Select
                  name="tenantType"
                  value={formData.tenantType || 0}
                  onChange={handleSelectChange}
                  label={t("tenants.typeLabel")}
                  required
                >
                  <MenuItem value={0} disabled>
                    {t("tenants.selectType")}
                  </MenuItem>
                  {tenantTypes.map((type) => (
                    <MenuItem key={type.tenantTypeId} value={type.tenantTypeId}>
                      {type.tenantTypeName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tenantType && <FormHelperText>{errors.tenantType}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.phone")}
                name="tenantPhone"
                required
                value={formData.tenantPhone || ''}
                onChange={handleInputChange}
                error={!!errors.tenantPhone}
                helperText={errors.tenantPhone}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("fields.address")}
                name="tenantAddress"
                required
                value={formData.tenantAddress || ''}
                onChange={handleInputChange}
                error={!!errors.tenantAddress}
                helperText={errors.tenantAddress}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.city")}
                name="tenantCity"
                required
                value={formData.tenantCity || ''}
                onChange={handleInputChange}
                error={!!errors.tenantCity}
                helperText={errors.tenantCity}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.state")}
                name="tenantState"
                required
                value={formData.tenantState || ''}
                onChange={handleInputChange}
                error={!!errors.tenantState}
                helperText={errors.tenantState}
                margin="normal"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.zipcode")}
                name="tenantZipcode"
                required
                value={formData.tenantZipcode || ''}
                onChange={handleInputChange}
                error={!!errors.tenantZipcode}
                helperText={
                  errors.tenantZipcode ||
                  t("common.formatLabel", {
                    mask: exemploCodigoPostal(formData.tenantCountry),
                  })
                }
                margin="normal"
                placeholder={exemploCodigoPostal(formData.tenantCountry)}
                inputProps={{ maxLength: maxLengthCodigoPostal(formData.tenantCountry) }}
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("fields.country")}
                name="tenantCountry"
                required
                value={formData.tenantCountry || ''}
                onChange={handleInputChange}
                error={!!errors.tenantCountry}
                helperText={errors.tenantCountry || t("tenants.countryHelper")}
                margin="normal"
                inputProps={{ maxLength: 2 }}
                placeholder="BR"
                disabled={isReadOnlyUser}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("fields.fiscalCode")}
                name="tenantFiscalCode"
                required
                value={formData.tenantFiscalCode || ''}
                onChange={handleInputChange}
                error={!!errors.tenantFiscalCode}
                helperText={
                  errors.tenantFiscalCode ||
                  descreverFormatoFiscal(formData.tenantCountry) ||
                  t("common.formatLabel", { mask: "00.000.000/0000-00" })
                }
                margin="normal"
                inputProps={{ maxLength: 24 }}
                disabled={isReadOnlyUser}
              />
            </Grid>

            {/* Idioma: nível do meio da cascata — vence o do parceiro, perde
                para a escolha do usuário. */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" disabled={isReadOnlyUser}>
                <InputLabel id="tenant-language-label">{t("common.language")}</InputLabel>
                <Select
                  labelId="tenant-language-label"
                  name="tenantLanguage"
                  label={t("common.language")}
                  value={(formData.tenantLanguage as string) ?? ''}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      tenantLanguage: e.target.value === '' ? null : String(e.target.value),
                    }))
                  }
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {supportedLanguages.map(l => (
                    <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t("partners.languageHelper")}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Vínculo com o parceiro. Travado para quem já pertence a um:
                mover um proprietário para outro parceiro é decisão do grupo
                raiz, não de quem está dentro de um deles. */}
            <Grid item xs={12} md={6}>
              {!podeEscolherParceiro ? (
                /* Sem a lista buscada, um Select com valor fora das opções
                   apareceria vazio (e o MUI reclamaria). Campo travado mostra
                   o vínculo real: o nome quando o servidor o derivou do id, o
                   próprio id quando não. */
                <TextField
                  fullWidth
                  margin="normal"
                  label={t("fields.partner")}
                  value={
                    (formData as any).tenantPartnerName?.toString().trim() ||
                    (formData.tenantPartnerId === null ||
                    formData.tenantPartnerId === undefined
                      ? "—"
                      : String(formData.tenantPartnerId))
                  }
                  helperText={t("partners.lockedHelper")}
                  disabled
                />
              ) : (
              <FormControl
                fullWidth
                margin="normal"
                disabled={isReadOnlyUser || carregandoParceiros}
              >
                <InputLabel id="tenant-partner-label">{t("fields.partner")}</InputLabel>
                <Select
                  labelId="tenant-partner-label"
                  name="tenantPartnerId"
                  label={t("fields.partner")}
                  value={
                    formData.tenantPartnerId === null ||
                    formData.tenantPartnerId === undefined
                      ? ''
                      : String(formData.tenantPartnerId)
                  }
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      // String vazia é "sem vínculo"; "0" é o grupo raiz.
                      tenantPartnerId:
                        e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                >
                  <MenuItem value=""><em>{t("partners.noneOption")}</em></MenuItem>
                  {parceiros.map(pc => (
                    <MenuItem key={pc.partnerId} value={String(pc.partnerId)}>
                      {pc.partnerName || `#${pc.partnerId}`}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t("partners.selectHelper")}</FormHelperText>
              </FormControl>
              )}
            </Grid>

            {isEditMode && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  sx={{ mt: 3 }}
                  control={
                    <Switch
                      checked={formData.tenantStatus !== 0}
                      disabled={isReadOnlyUser}
                      onChange={(e) =>
                        setFormData(prev => ({
                          ...prev,
                          tenantStatus: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label={t("fields.enabled")}
                />
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
              disabled={saving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || isReadOnlyUser}
              startIcon={<SaveIcon />}
              sx={{ width: isMobile ? "100%" : "auto" }}
            >
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
}